import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ShieldCheck, Building2, Users, BarChart3, Cpu, Flag, FileText,
  Loader2, Search, UserCheck, UserX, UserCog, Eye, AlertTriangle,
  Mail, CheckCircle2, XCircle, RefreshCw, HelpCircle, PlayCircle, Save,
  Sparkles, BookOpen, Plus, Trash2, ArrowUp, ArrowDown,
  MessageSquare, Tag,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { api, errorMessage, tokenStore, orgStore, IMPERSONATION_KEY } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { ModuleGuide } from "@/components/ModuleGuide";
import { ADMIN_GUIDE } from "@/lib/guides";

function fmtUSD(c) { return new Intl.NumberFormat(undefined, { style: "currency", currency: "USD" }).format((c || 0) / 100); }

export default function Admin() {
  const { user } = useAuth();
  const [tab, setTab] = useState("orgs");
  if (!user?.is_super_admin) {
    return (
      <div className="mx-auto max-w-md p-12 text-center text-white/65" data-testid="admin-forbidden">
        <AlertTriangle className="mx-auto h-10 w-10 text-amber-300" />
        <h1 className="font-display mt-4 text-2xl font-light">Super-admin only</h1>
        <p className="mt-2 text-sm">This area is restricted to platform administrators.</p>
      </div>
    );
  }
  return (
    <div className="mx-auto max-w-7xl">
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
        <div className="text-eyebrow text-emerald-300">Platform admin</div>
        <h1 className="font-display mt-2 text-4xl font-light tracking-tight sm:text-5xl">
          <span className="brand-text">Control plane</span>
        </h1>
        <p className="mt-2 max-w-xl text-white/55">Tenant ops, bans, model toggles, feature flags, and audit trail.</p>
      </motion.div>
      <Tabs value={tab} onValueChange={setTab} className="mt-6">
        <TabsList className="bg-white/5 text-white flex-wrap" data-testid="admin-tabs">
          <TabsTrigger value="orgs" data-testid="admin-tab-orgs" className="data-[state=active]:bg-white data-[state=active]:text-black"><Building2 className="mr-1 h-3.5 w-3.5" /> Tenants</TabsTrigger>
          <TabsTrigger value="users" data-testid="admin-tab-users" className="data-[state=active]:bg-white data-[state=active]:text-black"><Users className="mr-1 h-3.5 w-3.5" /> Users</TabsTrigger>
          <TabsTrigger value="usage" data-testid="admin-tab-usage" className="data-[state=active]:bg-white data-[state=active]:text-black"><BarChart3 className="mr-1 h-3.5 w-3.5" /> Usage</TabsTrigger>
          <TabsTrigger value="models" data-testid="admin-tab-models" className="data-[state=active]:bg-white data-[state=active]:text-black"><Cpu className="mr-1 h-3.5 w-3.5" /> AI Models</TabsTrigger>
          <TabsTrigger value="flags" data-testid="admin-tab-flags" className="data-[state=active]:bg-white data-[state=active]:text-black"><Flag className="mr-1 h-3.5 w-3.5" /> Flags</TabsTrigger>
          <TabsTrigger value="logs" data-testid="admin-tab-logs" className="data-[state=active]:bg-white data-[state=active]:text-black"><FileText className="mr-1 h-3.5 w-3.5" /> Logs</TabsTrigger>
          <TabsTrigger value="email" data-testid="admin-tab-email" className="data-[state=active]:bg-white data-[state=active]:text-black"><Mail className="mr-1 h-3.5 w-3.5" /> Email</TabsTrigger>
          <TabsTrigger value="platform" data-testid="admin-tab-platform" className="data-[state=active]:bg-white data-[state=active]:text-black"><PlayCircle className="mr-1 h-3.5 w-3.5" /> Platform</TabsTrigger>
          <TabsTrigger value="playbook" data-testid="admin-tab-playbook" className="data-[state=active]:bg-white data-[state=active]:text-black"><BookOpen className="mr-1 h-3.5 w-3.5" /> Playbook</TabsTrigger>
          <TabsTrigger value="public-sam" data-testid="admin-tab-public-sam" className="data-[state=active]:bg-white data-[state=active]:text-black"><MessageSquare className="mr-1 h-3.5 w-3.5" /> Public Sam Leads</TabsTrigger>
          <TabsTrigger value="promos" data-testid="admin-tab-promos" className="data-[state=active]:bg-white data-[state=active]:text-black"><Tag className="mr-1 h-3.5 w-3.5" /> Promo Codes</TabsTrigger>
          <TabsTrigger value="guide" data-testid="admin-tab-guide" className="data-[state=active]:bg-white data-[state=active]:text-black"><HelpCircle className="mr-1 h-3.5 w-3.5" /> Guide</TabsTrigger>
        </TabsList>
        <TabsContent value="orgs" className="mt-5"><OrgsTab /></TabsContent>
        <TabsContent value="users" className="mt-5"><UsersTab /></TabsContent>
        <TabsContent value="usage" className="mt-5"><UsageTab /></TabsContent>
        <TabsContent value="models" className="mt-5"><ModelsTab /></TabsContent>
        <TabsContent value="flags" className="mt-5"><FlagsTab /></TabsContent>
        <TabsContent value="logs" className="mt-5"><LogsTab /></TabsContent>
        <TabsContent value="email" className="mt-5"><EmailTab /></TabsContent>
        <TabsContent value="platform" className="mt-5"><PlatformTab /></TabsContent>
        <TabsContent value="playbook" className="mt-5"><PlaybookAdminTab /></TabsContent>
        <TabsContent value="public-sam" className="mt-5"><PublicSamLeadsTab /></TabsContent>
        <TabsContent value="promos" className="mt-5"><PromoCodesTab /></TabsContent>
        <TabsContent value="guide" className="mt-5">
          <ModuleGuide
            title={ADMIN_GUIDE.title}
            whatItIs={ADMIN_GUIDE.whatItIs}
            capabilities={ADMIN_GUIDE.capabilities}
            howTo={ADMIN_GUIDE.howTo}
            ctaLabel={ADMIN_GUIDE.cta}
            onCta={() => setTab("orgs")}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function OrgsTab() {
  const [rows, setRows] = useState([]); const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null); const [detail, setDetail] = useState(null);
  const load = useCallback(() => { setLoading(true); api.get("/admin/orgs").then((r) => setRows(r.data)).catch((e) => toast.error(errorMessage(e))).finally(() => setLoading(false)); }, []);
  useEffect(() => { load(); }, [load]);
  const open = async (org) => { setOpenId(org.id); setDetail(null); try { const r = await api.get(`/admin/orgs/${org.id}`); setDetail(r.data); } catch (e) { toast.error(errorMessage(e)); } };
  const setStatus = async (org_id, status) => {
    try { await api.patch(`/admin/orgs/${org_id}`, { status }); toast.success("Updated"); load(); if (openId === org_id) open({ id: org_id }); }
    catch (e) { toast.error(errorMessage(e)); }
  };

  if (loading) return <div className="flex items-center justify-center py-12 text-white/45"><Loader2 className="h-4 w-4 animate-spin" /></div>;
  return (
    <>
      <div className="glass overflow-hidden rounded-2xl" data-testid="admin-orgs-table">
        <table className="w-full text-sm">
          <thead className="bg-white/5 text-white/55"><tr>
            <th className="px-4 py-2 text-left">Name</th>
            <th className="px-4 py-2 text-left">Plan</th>
            <th className="px-4 py-2 text-right">Seats</th>
            <th className="px-4 py-2 text-right">MRR</th>
            <th className="px-4 py-2 text-right">Members</th>
            <th className="px-4 py-2 text-left">Status</th>
          </tr></thead>
          <tbody>
            {rows.map((o) => (
              <tr key={o.id} onClick={() => open(o)} className="cursor-pointer border-t border-white/5 hover:bg-white/[0.03]" data-testid={`admin-org-row-${o.id}`}>
                <td className="px-4 py-3 text-white">{o.name}</td>
                <td className="px-4 py-3 text-white/65"><span className="rounded-full border border-white/10 bg-white/5 px-2 py-0.5 text-[10px] uppercase tracking-wider">{o.plan}</span> · {o.subscription_status}</td>
                <td className="px-4 py-3 text-right text-white/85">{o.seat_count}</td>
                <td className="px-4 py-3 text-right text-fuchsia-300">{fmtUSD(o.monthly_total_cents)}</td>
                <td className="px-4 py-3 text-right text-white/55">{o.member_count}</td>
                <td className="px-4 py-3"><span className={`rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-wider ${o.status === "suspended" ? "border-red-500/30 bg-red-500/10 text-red-300" : "border-emerald-500/30 bg-emerald-500/10 text-emerald-300"}`}>{o.status || "active"}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Sheet open={!!openId} onOpenChange={(o) => !o && (setOpenId(null), setDetail(null))}>
        <SheetContent side="right" className="w-full max-w-2xl overflow-y-auto border-white/10 bg-[#0a0a0a] text-white" data-testid="admin-org-drawer">
          {detail ? (
            <>
              <SheetHeader>
                <SheetTitle className="font-display text-2xl">{detail.org.name}</SheetTitle>
                <SheetDescription className="text-white/55">{detail.org.id}</SheetDescription>
              </SheetHeader>
              <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                <Stat label="Plan" value={detail.org.plan} />
                <Stat label="Status" value={detail.org.subscription_status} />
                <Stat label="Seats" value={String(detail.org.seat_count)} />
                <Stat label="MRR" value={fmtUSD(detail.org.monthly_total_cents)} />
                <Stat label="AI msgs (30d)" value={String(detail.usage_30d.ai_messages)} />
                <Stat label="Content (30d)" value={String(detail.usage_30d.content_generations)} />
              </div>
              <div className="mt-5">
                <div className="text-eyebrow text-white/40">Members · {detail.members.length}</div>
                <ul className="mt-2 space-y-1">
                  {detail.members.map((m) => (
                    <li key={m.user_id} className="flex items-center justify-between rounded-xl border border-white/5 bg-white/[0.02] px-3 py-2 text-sm">
                      <div><span className="text-white">{m.name}</span> <span className="text-white/45">· {m.email}</span></div>
                      <span className="rounded-full border border-white/10 bg-white/5 px-2 py-0.5 text-[10px] uppercase tracking-wider">{m.role}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="mt-5 flex gap-2">
                {detail.org.status === "suspended" ? (
                  <Button onClick={() => setStatus(detail.org.id, "active")} className="rounded-xl bg-white text-black hover:bg-zinc-200" data-testid="admin-org-unsuspend">Reactivate org</Button>
                ) : (
                  <Button onClick={() => setStatus(detail.org.id, "suspended")} variant="outline" className="border-red-500/40 bg-red-500/10 text-red-200 hover:bg-red-500/20" data-testid="admin-org-suspend">Suspend org</Button>
                )}
              </div>
            </>
          ) : <div className="flex items-center justify-center py-12 text-white/45"><Loader2 className="h-4 w-4 animate-spin" /></div>}
        </SheetContent>
      </Sheet>
    </>
  );
}

function UsersTab() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]); const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [banTarget, setBanTarget] = useState(null);
  const [impTarget, setImpTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/users${q ? `?q=${encodeURIComponent(q)}` : ""}`);
      setRows(r.data);
    } catch (err) { toast.error(errorMessage(err)); }
    finally { setLoading(false); }
  }, [q]);
  useEffect(() => { load(); }, [load]);

  const ban = async () => {
    if (!banTarget) return;
    try { await api.post(`/admin/users/${banTarget.id}/ban`); toast.success("Banned"); setBanTarget(null); load(); }
    catch (e) { toast.error(errorMessage(e)); }
  };
  const unban = async (id) => {
    try { await api.post(`/admin/users/${id}/unban`); toast.success("Unbanned"); load(); } catch (e) { toast.error(errorMessage(e)); }
  };
  const confirmImpersonate = async () => {
    const u = impTarget;
    if (!u) return;
    try {
      const r = await api.post(`/admin/users/${u.id}/impersonate`);
      const { token, impersonated_user, impersonated_org_id } = r.data;
      // Save real admin token first
      const realToken = tokenStore.get();
      const realOrg = orgStore.get();
      localStorage.setItem(IMPERSONATION_KEY, JSON.stringify({ realToken, realOrg, impersonatedEmail: impersonated_user.email }));
      tokenStore.set(token);
      orgStore.set(impersonated_org_id);
      setImpTarget(null);
      toast.success(`Impersonating ${impersonated_user.email}`);
      // Hard reload to /app to re-bootstrap auth context
      window.location.href = "/app";
    } catch (e) { toast.error(errorMessage(e)); setImpTarget(null); }
  };

  return (
    <>
      <div className="flex items-center gap-2">
        <Search className="h-4 w-4 text-white/40" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search email or name" className="max-w-md border-white/10 bg-black/40 text-white" data-testid="admin-users-search" />
      </div>
      {loading ? (
        <div className="mt-6 flex items-center justify-center py-12 text-white/45"><Loader2 className="h-4 w-4 animate-spin" /></div>
      ) : (
        <div className="glass mt-4 overflow-hidden rounded-2xl" data-testid="admin-users-table">
          <table className="w-full text-sm">
            <thead className="bg-white/5 text-white/55"><tr>
              <th className="px-4 py-2 text-left">User</th>
              <th className="px-4 py-2 text-left">Org</th>
              <th className="px-4 py-2 text-left">Status</th>
              <th className="px-4 py-2 text-right">Actions</th>
            </tr></thead>
            <tbody>
              {rows.map((u) => (
                <tr key={u.id} className="border-t border-white/5">
                  <td className="px-4 py-3">
                    <div className="text-white">{u.name} {u.is_super_admin && <span className="ml-1 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-1.5 py-0.5 text-[10px] text-emerald-300">SUPER</span>}</div>
                    <div className="text-xs text-white/45">{u.email}</div>
                  </td>
                  <td className="px-4 py-3 text-white/65">{u.primary_org_name || "—"}</td>
                  <td className="px-4 py-3"><span className={`rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-wider ${u.status === "banned" ? "border-red-500/30 bg-red-500/10 text-red-300" : "border-emerald-500/30 bg-emerald-500/10 text-emerald-300"}`}>{u.status}</span></td>
                  <td className="px-4 py-3 text-right">
                    {!u.is_super_admin && (
                      <div className="flex justify-end gap-1">
                        <button onClick={() => setImpTarget(u)} className="rounded p-1.5 text-white/55 hover:bg-white/10 hover:text-white" title="Impersonate" data-testid={`admin-impersonate-${u.id}`}><UserCog className="h-3.5 w-3.5" /></button>
                        {u.status === "banned" ? (
                          <button onClick={() => unban(u.id)} className="rounded p-1.5 text-emerald-300 hover:bg-emerald-500/10" title="Unban" data-testid={`admin-unban-${u.id}`}><UserCheck className="h-3.5 w-3.5" /></button>
                        ) : (
                          <button onClick={() => setBanTarget(u)} className="rounded p-1.5 text-red-300 hover:bg-red-500/10" title="Ban" data-testid={`admin-ban-${u.id}`}><UserX className="h-3.5 w-3.5" /></button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <AlertDialog open={!!banTarget} onOpenChange={(o) => !o && setBanTarget(null)}>
        <AlertDialogContent className="border-white/10 bg-[#0a0a0a] text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Ban {banTarget?.email}?</AlertDialogTitle>
            <AlertDialogDescription className="text-white/55">Their existing JWT will stop working on the next API call.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-white/15 bg-transparent text-white">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={ban} className="bg-red-500 text-white hover:bg-red-600" data-testid="admin-ban-confirm">Ban</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!impTarget} onOpenChange={(o) => !o && setImpTarget(null)}>
        <AlertDialogContent className="border-white/10 bg-[#0a0a0a] text-white" data-testid="impersonate-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Impersonate {impTarget?.email}?</AlertDialogTitle>
            <AlertDialogDescription className="text-white/55">
              You'll switch into this user's workspace for 30 minutes. Every action is
              audit-logged with your admin id as the impersonator. Use the sticky banner
              at the top of the screen to exit at any time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-white/15 bg-transparent text-white" data-testid="impersonate-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmImpersonate} className="bg-amber-500 text-amber-950 hover:bg-amber-400" data-testid="impersonate-confirm">
              Yes, impersonate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function UsageTab() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/usage").then((r) => setData(r.data)).catch((e) => toast.error(errorMessage(e))); }, []);
  if (!data) return <div className="flex items-center justify-center py-12 text-white/45"><Loader2 className="h-4 w-4 animate-spin" /></div>;
  return (
    <div className="space-y-4" data-testid="admin-usage">
      <div className="grid gap-4 sm:grid-cols-3 lg:grid-cols-6">
        <Stat label="Orgs" value={data.orgs_total} />
        <Stat label="Users" value={data.users_total} />
        <Stat label="Agents" value={data.agents_total} />
        <Stat label="Sequences" value={data.sequences_total} />
        <Stat label="Leads" value={data.leads_total} />
        <Stat label="KB docs" value={data.kb_docs_total} />
      </div>
      <div className="glass rounded-2xl p-5">
        <div className="text-eyebrow text-white/40">AI events by model (last 7 days)</div>
        <ul className="mt-3 space-y-2 text-sm">
          {data.ai_events_by_model_7d.length === 0 && <li className="text-white/45">No events yet.</li>}
          {data.ai_events_by_model_7d.map((r) => {
            const max = Math.max(...data.ai_events_by_model_7d.map((x) => x.count), 1);
            return (
              <li key={r.model} className="flex items-center gap-3">
                <span className="w-40 text-white">{r.model}</span>
                <div className="h-2 flex-1 overflow-hidden rounded-full bg-white/5"><div className="h-full brand-bg" style={{ width: `${(r.count / max) * 100}%` }} /></div>
                <span className="w-12 text-right text-white/55">{r.count}</span>
              </li>
            );
          })}
        </ul>
      </div>
      <div className="glass rounded-2xl p-5">
        <div className="text-eyebrow text-white/40">Top orgs by activity (30d)</div>
        <ul className="mt-3 space-y-1 text-sm">
          {data.top_orgs_30d.length === 0 && <li className="text-white/45">No activity yet.</li>}
          {data.top_orgs_30d.map((r) => (
            <li key={r.org_id} className="flex items-center justify-between border-b border-white/5 py-1 last:border-0">
              <span className="text-white">{r.org_name}</span>
              <span className="text-white/55">{r.count}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function ModelsTab() {
  const [rows, setRows] = useState([]); const [loading, setLoading] = useState(true);
  const load = () => { setLoading(true); api.get("/admin/models").then((r) => setRows(r.data)).catch((e) => toast.error(errorMessage(e))).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);
  const toggle = async (row, v) => {
    try { await api.patch(`/admin/models/${row.model_id}`, { is_enabled: v }); load(); }
    catch (e) { toast.error(errorMessage(e)); }
  };
  if (loading) return <div className="flex items-center justify-center py-12 text-white/45"><Loader2 className="h-4 w-4 animate-spin" /></div>;
  return (
    <div className="glass overflow-hidden rounded-2xl" data-testid="admin-models">
      <table className="w-full text-sm">
        <thead className="bg-white/5 text-white/55"><tr><th className="px-4 py-2 text-left">Model</th><th className="px-4 py-2 text-left">Provider</th><th className="px-4 py-2 text-right">Enabled</th></tr></thead>
        <tbody>
          {rows.map((m) => (
            <tr key={m.model_id} className="border-t border-white/5">
              <td className="px-4 py-3"><div className="text-white">{m.display_name}</div><div className="text-xs text-white/45">{m.model_id}</div></td>
              <td className="px-4 py-3 text-white/65">{m.provider}</td>
              <td className="px-4 py-3 text-right"><Switch checked={m.is_enabled} onCheckedChange={(v) => toggle(m, v)} data-testid={`admin-model-toggle-${m.model_id}`} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function FlagsTab() {
  const [rows, setRows] = useState([]); const [loading, setLoading] = useState(true);
  const load = () => { setLoading(true); api.get("/admin/flags").then((r) => setRows(r.data)).catch((e) => toast.error(errorMessage(e))).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);
  const toggle = async (key, value, description) => {
    try { await api.patch(`/admin/flags/${key}`, { value, description }); load(); toast.success("Updated"); }
    catch (e) { toast.error(errorMessage(e)); }
  };
  if (loading) return <div className="flex items-center justify-center py-12 text-white/45"><Loader2 className="h-4 w-4 animate-spin" /></div>;
  return (
    <div className="space-y-2" data-testid="admin-flags">
      {rows.length === 0 && <div className="text-white/45">No flags defined.</div>}
      {rows.map((f) => {
        const isBool = typeof f.value === "boolean";
        return (
          <div key={f.key} className="glass rounded-xl p-4">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="font-mono text-sm text-white">{f.key}</div>
                <div className="text-xs text-white/45">{f.description || "—"}</div>
              </div>
              {isBool ? (
                <Switch checked={f.value} onCheckedChange={(v) => toggle(f.key, v, f.description)} data-testid={`admin-flag-toggle-${f.key}`} />
              ) : (
                <code className="rounded bg-white/5 px-2 py-1 text-xs text-fuchsia-200">{JSON.stringify(f.value)}</code>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function LogsTab() {
  const [rows, setRows] = useState([]); const [loading, setLoading] = useState(true);
  useEffect(() => { api.get("/admin/logs?limit=200").then((r) => setRows(r.data)).catch((e) => toast.error(errorMessage(e))).finally(() => setLoading(false)); }, []);
  if (loading) return <div className="flex items-center justify-center py-12 text-white/45"><Loader2 className="h-4 w-4 animate-spin" /></div>;
  return (
    <div className="glass overflow-hidden rounded-2xl" data-testid="admin-logs">
      <table className="w-full text-sm">
        <thead className="bg-white/5 text-white/55"><tr>
          <th className="px-4 py-2 text-left">When</th><th className="px-4 py-2 text-left">Action</th>
          <th className="px-4 py-2 text-left">Actor</th><th className="px-4 py-2 text-left">Target</th>
        </tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-t border-white/5">
              <td className="px-4 py-3 text-white/55 whitespace-nowrap">{new Date(r.created_at).toLocaleString()}</td>
              <td className="px-4 py-3 font-mono text-xs text-fuchsia-200">{r.action}</td>
              <td className="px-4 py-3">{r.actor_email || <span className="text-white/45">system</span>}{r.impersonator_email && <span className="text-amber-300 text-xs"> via {r.impersonator_email}</span>}</td>
              <td className="px-4 py-3 text-white/65">{r.target_type ? `${r.target_type}/${r.target_id?.slice(0, 8) || "—"}` : "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-2xl border border-white/5 bg-white/[0.02] p-4">
      <div className="text-eyebrow text-white/40">{label}</div>
      <div className="font-display mt-1 text-2xl font-light text-white">{value}</div>
    </div>
  );
}

function EmailTab() {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [probing, setProbing] = useState(false);
  const [filter, setFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [webhook, setWebhook] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams({ limit: "150" });
      if (filter) qs.set("q", filter);
      if (statusFilter) qs.set("status", statusFilter);
      const [s, l, w] = await Promise.all([
        api.get("/admin/email/status"),
        api.get(`/admin/email/logs?${qs}`),
        api.get("/admin/email/webhook"),
      ]);
      setStatus(s.data);
      setRows(l.data.rows || []);
      setCounts(l.data.counts || {});
      setWebhook(w.data);
    } catch (e) { toast.error(errorMessage(e)); }
    finally { setLoading(false); }
  }, [filter, statusFilter]);
  useEffect(() => { load(); }, [load]);

  const copyWebhookUrl = async () => {
    if (!webhook?.url) return;
    try {
      await navigator.clipboard.writeText(webhook.url);
      toast.success("Webhook URL copied");
    } catch { toast.error("Couldn't copy — select manually"); }
  };

  const sendProbe = async () => {
    setProbing(true);
    try {
      const res = await api.post("/admin/email/probe", { to: user?.email });
      if (res.data?.ok) toast.success(`Probe sent · message_id ${res.data.message_id?.slice(0, 12) || "?"}`);
      else if (res.data?.unverified_domain) toast.error("Resend reports the from-domain is NOT verified — check DNS records.");
      else if (res.data?.rate_limited) toast.error("Resend rate limit reached.");
      else toast.error(res.data?.error || "Probe failed");
      await load();
    } catch (e) { toast.error(errorMessage(e)); }
    finally { setProbing(false); }
  };

  const statusPill = (s) => {
    const map = {
      sent:                 ["bg-emerald-500/15 text-emerald-300 border-emerald-500/30", "Sent"],
      failed:               ["bg-red-500/15 text-red-300 border-red-500/30", "Failed"],
      unverified_domain:    ["bg-amber-500/15 text-amber-300 border-amber-500/30", "Unverified domain"],
      rate_limited:         ["bg-amber-500/15 text-amber-300 border-amber-500/30", "Rate-limited"],
      skipped_unsubscribed: ["bg-white/10 text-white/60 border-white/15", "Unsubscribed"],
    };
    const [cls, lbl] = map[s] || ["bg-white/10 text-white/60 border-white/15", s];
    return <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-wider ${cls}`}>{lbl}</span>;
  };

  const dvBadge = status?.domain_verified === true
    ? <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 text-xs text-emerald-300"><CheckCircle2 className="h-3 w-3" /> Domain verified</span>
    : status?.domain_verified === false
    ? <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-500/30 bg-amber-500/10 px-2 py-0.5 text-xs text-amber-300"><AlertTriangle className="h-3 w-3" /> Domain not verified</span>
    : <span className="inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/5 px-2 py-0.5 text-xs text-white/55">Status unknown — send a probe</span>;

  return (
    <div className="space-y-6" data-testid="email-tab">
      <div className="glass rounded-2xl p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-indigo-300" />
              <div className="font-display text-lg font-medium">Resend transactional email</div>
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-white/55">
              <span>From: <span className="text-white/80">{status?.from_name} &lt;{status?.from_email}&gt;</span></span>
              <span>Reply-to: <span className="text-white/80">{status?.reply_to}</span></span>
              <span>Sends today: <span className="text-white/80">{status?.sends_today ?? 0}</span></span>
            </div>
            <div className="mt-3">{dvBadge}</div>
            {status?.last_error && (
              <div className="mt-2 max-w-xl text-xs text-amber-200/85">Last error: {status.last_error}</div>
            )}
          </div>
          <div className="flex gap-2">
            <Button onClick={load} variant="outline" className="rounded-full border-white/15 bg-white/5 text-white hover:bg-white/10" data-testid="email-refresh">
              <RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Refresh
            </Button>
            <Button onClick={sendProbe} disabled={probing} className="rounded-full bg-white text-black hover:bg-zinc-200" data-testid="email-probe-btn">
              {probing && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
              Send probe to {user?.email}
            </Button>
          </div>
        </div>
      </div>

      {/* Webhook card */}
      <div className="glass rounded-2xl p-6" data-testid="webhook-card">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-indigo-300" />
              <div className="font-display text-lg font-medium">Resend engagement webhook</div>
              {webhook?.secret_configured ? (
                <span className="inline-flex items-center gap-1 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 text-[10px] uppercase tracking-wider text-emerald-300" data-testid="webhook-secret-ok">
                  <CheckCircle2 className="h-3 w-3" /> Configured
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 rounded-full border border-amber-500/30 bg-amber-500/10 px-2 py-0.5 text-[10px] uppercase tracking-wider text-amber-300" data-testid="webhook-secret-missing">
                  <AlertTriangle className="h-3 w-3" /> Not configured
                </span>
              )}
              {webhook?.simulate_engagement && (
                <span className="inline-flex items-center gap-1 rounded-full border border-white/15 bg-white/5 px-2 py-0.5 text-[10px] uppercase tracking-wider text-white/55" title="SIMULATE_SEQUENCE_ENGAGEMENT=true">
                  Sim mode
                </span>
              )}
            </div>
            <p className="mt-2 max-w-xl text-xs text-white/55">
              Register this URL in your Resend dashboard → Webhooks → Add endpoint. Select all 7 event types below. Reveal the signing secret in Resend and paste it as <code className="text-white/85">RESEND_WEBHOOK_SECRET</code> in <code className="text-white/85">backend/.env</code>, then <code className="text-white/85">sudo supervisorctl restart backend</code>.
            </p>
            <div className="mt-3 flex items-center gap-2">
              <code className="rounded-md border border-white/10 bg-black/40 px-2 py-1 text-xs text-indigo-200 break-all" data-testid="webhook-url">
                {webhook?.url || "—"}
              </code>
              <Button onClick={copyWebhookUrl} variant="outline" size="sm" className="rounded-full border-white/15 bg-white/5 text-white hover:bg-white/10" data-testid="webhook-copy">
                Copy URL
              </Button>
            </div>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {(webhook?.events || []).map((evt) => (
                <span key={evt} className="rounded-full border border-white/10 bg-white/5 px-2 py-0.5 text-[10px] font-mono text-white/65">
                  {evt}
                </span>
              ))}
            </div>
          </div>
          <div className="text-right text-xs">
            <div className="text-[10px] uppercase tracking-wider text-white/40">Events received (last 1000)</div>
            <div className="font-display mt-1 text-2xl font-light" data-testid="webhook-counts-total">
              {Object.values(webhook?.counts || {}).reduce((a, b) => a + b, 0)}
            </div>
            {Object.keys(webhook?.counts || {}).length > 0 && (
              <div className="mt-2 space-y-0.5 text-white/55">
                {Object.entries(webhook.counts).slice(0, 5).map(([k, v]) => (
                  <div key={k}><span className="font-mono text-white/70">{k}</span>: {v}</div>
                ))}
              </div>
            )}
          </div>
        </div>

        {webhook?.recent?.length > 0 && (
          <div className="mt-5 border-t border-white/5 pt-4">
            <div className="mb-2 text-[10px] uppercase tracking-wider text-white/40">Last received events</div>
            <div className="space-y-1.5 text-xs">
              {webhook.recent.slice(0, 8).map((ev) => (
                <div key={ev.svix_id} className="flex flex-wrap items-center gap-2 rounded-lg border border-white/5 bg-white/[0.02] px-3 py-1.5" data-testid={`webhook-event-${ev.svix_id}`}>
                  <span className="font-mono text-indigo-300">{ev.type}</span>
                  <span className="text-white/55">{ev.subject || ev.message_id || "—"}</span>
                  <span className="ml-auto text-white/40">{new Date(ev.received_at).toLocaleString()}</span>
                  {ev.status === "error" && <XCircle className="h-3 w-3 text-red-400" title={ev.error} />}
                  {ev.status === "processed" && <CheckCircle2 className="h-3 w-3 text-emerald-400" />}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="glass rounded-2xl p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="font-display text-lg font-medium">Recent sends</div>
          <div className="flex flex-wrap gap-2">
            {Object.entries(counts).map(([k, v]) => (
              <button
                key={k}
                onClick={() => setStatusFilter(k === statusFilter ? "" : k)}
                className={`rounded-full border px-2.5 py-1 text-[11px] transition ${
                  statusFilter === k
                    ? "border-indigo-400/60 bg-indigo-500/15 text-white"
                    : "border-white/10 bg-white/5 text-white/65 hover:bg-white/10"
                }`}
                data-testid={`email-filter-${k}`}
              >
                {k}: {v}
              </button>
            ))}
            <Input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Search recipient or subject…"
              className="h-8 max-w-xs border-white/10 bg-black/40 text-white placeholder:text-white/40"
              data-testid="email-search"
            />
          </div>
        </div>

        {loading ? (
          <div className="mt-6 flex items-center gap-2 text-sm text-white/45"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…</div>
        ) : rows.length === 0 ? (
          <div className="mt-6 text-sm text-white/45">No sends yet. Trigger a signup, invite, or password reset to populate.</div>
        ) : (
          <div className="mt-5 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/5 text-left text-[11px] uppercase tracking-wider text-white/40">
                  <th className="py-2 pr-3">Sent</th>
                  <th className="py-2 pr-3">To</th>
                  <th className="py-2 pr-3">Subject</th>
                  <th className="py-2 pr-3">Template</th>
                  <th className="py-2 pr-3">Status</th>
                  <th className="py-2 pr-3">Message ID</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`email-row-${r.id}`}>
                    <td className="py-2 pr-3 text-white/55 text-xs">{new Date(r.sent_at).toLocaleString()}</td>
                    <td className="py-2 pr-3 text-white/85">{Array.isArray(r.to) ? r.to.join(", ") : r.to}</td>
                    <td className="py-2 pr-3 text-white/85">{r.subject}</td>
                    <td className="py-2 pr-3 text-white/55">{r.template || "—"}</td>
                    <td className="py-2 pr-3">{statusPill(r.status)}</td>
                    <td className="py-2 pr-3 font-mono text-[11px] text-white/55">{r.message_id || (r.error ? <XCircle className="inline h-3 w-3 text-red-400" title={r.error} /> : "—")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}


function PlatformTab() {
  const [value, setValue] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedTick, setSavedTick] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/admin/platform-settings/welcome_video_url");
        setValue(r.data?.value || "");
      } catch (e) {
        // 403 means non-super-admin
        if (e?.response?.status !== 403) toast.error(errorMessage(e));
      } finally { setLoaded(true); }
    })();
  }, [savedTick]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/admin/platform-settings/welcome_video_url", { value: value.trim() || null });
      toast.success("Welcome video URL saved");
      setSavedTick((t) => t + 1);
    } catch (e) { toast.error(errorMessage(e)); }
    finally { setSaving(false); }
  };

  const detected = detectVideoLabel(value);

  if (!loaded) {
    return <div className="flex items-center justify-center py-16 text-white/45"><Loader2 className="h-5 w-5 animate-spin" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="platform-tab">
      <StripeModeBadge />
      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
        <div className="flex items-center gap-2 text-eyebrow text-fuchsia-300">
          <PlayCircle className="h-3.5 w-3.5" /> Welcome video
        </div>
        <h3 className="font-display mt-2 text-xl font-light text-white">Welcome tour video URL</h3>
        <p className="mt-1 text-sm text-white/55">
          Shown to every new user on their first sign-in (slide 1 of the welcome tour). Accepts
          YouTube, Loom, Vimeo, or a direct MP4 / WebM / MOV URL. Leave blank to show a placeholder.
        </p>

        <div className="mt-4 flex flex-col gap-3 sm:flex-row">
          <input
            type="url"
            placeholder="https://youtu.be/… · https://loom.com/share/… · https://vimeo.com/… · https://cdn.example.com/welcome.mp4"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            className="flex-1 rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-sm text-white outline-none placeholder:text-white/30 focus:border-fuchsia-500/40"
            data-testid="platform-welcome-video-input"
          />
          <button
            onClick={save}
            disabled={saving}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-white px-4 py-2 text-sm font-medium text-black hover:bg-zinc-200 disabled:opacity-60"
            data-testid="platform-welcome-video-save"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save
          </button>
        </div>

        <div className="mt-3 text-xs">
          {value.trim() === "" ? (
            <span className="text-white/40" data-testid="platform-welcome-video-status">No URL set — users will see the placeholder.</span>
          ) : detected ? (
            <span className="text-emerald-300" data-testid="platform-welcome-video-status">
              <CheckCircle2 className="mr-1 inline h-3.5 w-3.5" /> Detected as <span className="font-medium">{detected}</span> — will embed.
            </span>
          ) : (
            <span className="text-amber-300" data-testid="platform-welcome-video-status">
              <AlertTriangle className="mr-1 inline h-3.5 w-3.5" /> URL doesn't match YouTube / Loom / Vimeo / MP4 — placeholder will show instead.
            </span>
          )}
        </div>
      </div>

      <SamTipsManager />
    </div>
  );
}

function StripeModeBadge() {
  const [cfg, setCfg] = useState(null);
  useEffect(() => {
    api.get("/billing/config").then((r) => setCfg(r.data)).catch(() => setCfg({ mode: "unknown" }));
  }, []);
  if (!cfg) return null;
  const mode = (cfg.mode || "unknown").toLowerCase();
  const isLive = mode === "live";
  const cls = isLive
    ? "border-emerald-500/25 bg-emerald-500/[0.06] text-emerald-200"
    : "border-amber-500/25 bg-amber-500/[0.05] text-amber-200";
  return (
    <div
      className={`flex flex-wrap items-center gap-2 rounded-2xl border px-4 py-2.5 text-xs ${cls}`}
      data-testid="stripe-mode-badge"
      data-mode={mode}
    >
      <span className="font-semibold uppercase tracking-wider">Stripe mode:</span>
      <span className="font-mono text-[11px] font-bold uppercase tracking-widest" data-testid="stripe-mode-value">
        {isLive ? "LIVE" : mode.toUpperCase()}
      </span>
      <span className="text-white/30">·</span>
      {isLive ? (
        <span>Real charges enabled.</span>
      ) : (
        <span>
          Use card <span className="font-mono">4242 4242 4242 4242</span>, any future expiry, any CVC, any ZIP for test purchases.
        </span>
      )}
    </div>
  );
}

function SamTipsManager() {
  const [tips, setTips] = useState(null);
  const [previewing, setPreviewing] = useState(null);
  const [window_, setWindow] = useState("7d");
  const [analytics, setAnalytics] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/admin/sam-tips");
      setTips(r.data || []);
    } catch (e) { toast.error(errorMessage(e)); }
  };

  const loadAnalytics = async (w) => {
    try {
      const r = await api.get(`/admin/sam-tips/analytics?window=${w}`);
      setAnalytics(r.data || null);
    } catch (e) { /* silent — analytics are non-blocking */ }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => { loadAnalytics(window_); }, [window_]);

  if (!tips) {
    return (
      <div className="rounded-2xl border border-white/5 bg-white/[0.02] p-6">
        <div className="flex items-center justify-center text-white/45"><Loader2 className="h-5 w-5 animate-spin" /></div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="sam-tips-manager">
      <div className="rounded-2xl border border-fuchsia-500/20 bg-fuchsia-500/[0.04] p-4 text-sm text-fuchsia-100/80">
        <div className="text-eyebrow text-fuchsia-300">Brand voice</div>
        <p className="mt-1">
          Keep it calm and plain. No "transform your business", no "10x", no exclamation
          marks. Sam stays helpful, not pushy.
        </p>
      </div>

      <div className="rounded-2xl border border-white/5 bg-white/[0.02] p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 text-eyebrow text-fuchsia-300">
              <Sparkles className="h-3.5 w-3.5" /> Sam Tip Cards
            </div>
            <h3 className="font-display mt-2 text-xl font-light text-white">Empty-state nudge copy</h3>
            <p className="mt-1 text-sm text-white/55">
              Each module shows this card to new users on its empty state. Override the message
              or prefill any time — leave blank to fall back to the default.
            </p>
          </div>
          <div
            className="inline-flex shrink-0 items-center gap-0.5 rounded-full border border-white/10 bg-white/[0.03] p-0.5 text-xs"
            data-testid="sam-tips-window-selector"
            role="group"
            aria-label="Analytics window"
          >
            {[
              { v: "24h", l: "24h" },
              { v: "7d",  l: "7d"  },
              { v: "30d", l: "30d" },
            ].map((opt) => (
              <button
                key={opt.v}
                type="button"
                onClick={() => setWindow(opt.v)}
                className={`rounded-full px-3 py-1 transition ${
                  window_ === opt.v
                    ? "bg-white text-black"
                    : "text-white/60 hover:bg-white/5 hover:text-white"
                }`}
                data-testid={`sam-tips-window-${opt.v}`}
                aria-pressed={window_ === opt.v}
              >
                {opt.l}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="space-y-3" data-testid="sam-tips-rows">
        {tips.map((row) => (
          <SamTipRow
            key={row.module}
            row={row}
            onChanged={load}
            onPreview={() => setPreviewing(row)}
            analytics={analytics?.modules?.[row.module] || null}
            previous={analytics?.previous_window?.[row.module] || null}
          />
        ))}
      </div>

      <SamTipPreviewDialog tip={previewing} onClose={() => setPreviewing(null)} />
    </div>
  );
}

function _trendArrow(currentCtr, previousCtr) {
  // currentCtr & previousCtr are floats 0..1 (or null). Compare in percentage
  // points; threshold is 3 pp (0.03). Returns { dir: "up"|"down"|"flat", glyph, cls }.
  if (currentCtr == null) return { dir: "flat", glyph: "—", cls: "text-white/35" };
  if (previousCtr == null) return { dir: "flat", glyph: "—", cls: "text-white/35" };
  const delta = currentCtr - previousCtr;
  if (delta > 0.03)  return { dir: "up",   glyph: "↑", cls: "text-emerald-400" };
  if (delta < -0.03) return { dir: "down", glyph: "↓", cls: "text-red-400" };
  return { dir: "flat", glyph: "—", cls: "text-white/35" };
}

function SamTipRow({ row, onChanged, onPreview, analytics, previous }) {
  const [enabled, setEnabled] = useState(row.enabled);
  const [message, setMessage] = useState(row.override_message ?? "");
  const [prefill, setPrefill] = useState(row.override_prefill ?? "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setEnabled(row.enabled);
    setMessage(row.override_message ?? "");
    setPrefill(row.override_prefill ?? "");
  }, [row]);

  const trimmedMsg = message.trim();
  const trimmedPre = prefill.trim();
  const msgInvalid = trimmedMsg !== "" && (trimmedMsg.length < 3 || trimmedMsg.length > 200);
  const preInvalid = trimmedPre !== "" && (trimmedPre.length < 3 || trimmedPre.length > 200);

  const save = async () => {
    if (msgInvalid || preInvalid) { toast.error("Each field must be 3–200 characters (or blank to reset)."); return; }
    setSaving(true);
    try {
      await api.patch(`/admin/sam-tips/${row.module}`, {
        enabled,
        message: trimmedMsg || null,
        prefill: trimmedPre || null,
      });
      toast.success(`${row.label} tip saved`);
      onChanged?.();
    } catch (e) { toast.error(errorMessage(e)); }
    finally { setSaving(false); }
  };

  const reset = async () => {
    setSaving(true);
    try {
      await api.delete(`/admin/sam-tips/${row.module}`);
      toast.success(`${row.label} reset to default`);
      onChanged?.();
    } catch (e) { toast.error(errorMessage(e)); }
    finally { setSaving(false); }
  };

  return (
    <div
      className="rounded-2xl border border-white/5 bg-white/[0.02] p-4"
      data-testid={`sam-tip-row-${row.module}`}
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <div className="font-display text-base text-white">{row.label}</div>
          <code className="rounded bg-white/5 px-1.5 py-0.5 text-[10px] text-white/45">{row.path}</code>
        </div>
        <label className="flex items-center gap-2 text-xs text-white/55">
          <span>Enabled</span>
          <Switch
            checked={enabled}
            onCheckedChange={setEnabled}
            data-testid={`sam-tip-enabled-${row.module}`}
          />
        </label>
      </div>

      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <div>
          <Label className="text-xs text-white/50">Tip message</Label>
          <input
            type="text"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={row.default_message}
            maxLength={200}
            className={`mt-1 w-full rounded-xl border bg-white/5 px-3 py-2 text-sm text-white outline-none placeholder:text-white/30 focus:border-fuchsia-500/40 ${msgInvalid ? "border-red-500/60" : "border-white/10"}`}
            data-testid={`sam-tip-message-${row.module}`}
          />
          <p className="mt-1 text-[10px] text-white/35">
            Default: <span className="italic">{row.default_message}</span>
          </p>
        </div>
        <div>
          <Label className="text-xs text-white/50">Prefill question</Label>
          <input
            type="text"
            value={prefill}
            onChange={(e) => setPrefill(e.target.value)}
            placeholder={row.default_prefill}
            maxLength={200}
            className={`mt-1 w-full rounded-xl border bg-white/5 px-3 py-2 text-sm text-white outline-none placeholder:text-white/30 focus:border-fuchsia-500/40 ${preInvalid ? "border-red-500/60" : "border-white/10"}`}
            data-testid={`sam-tip-prefill-${row.module}`}
          />
          <p className="mt-1 text-[10px] text-white/35">
            Default: <span className="italic">{row.default_prefill}</span>
          </p>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-[10px] text-white/40">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <span data-testid={`sam-tip-meta-${row.module}`}>
            {row.updated_at ? (
              <>Last saved {new Date(row.updated_at).toLocaleString()} {row.updated_by ? `· by ${row.updated_by.slice(0, 8)}…` : ""}</>
            ) : "Never customised — using defaults."}
          </span>
          <span
            className="inline-flex items-center gap-1.5 text-[11px] text-white/55"
            data-testid={`sam-tip-analytics-${row.module}`}
          >
            <span aria-hidden="true" className="text-white/25">·</span>
            {(() => {
              const imp = analytics?.impressions ?? 0;
              const clk = analytics?.clicks ?? 0;
              const ctr = analytics?.ctr;
              if (imp === 0) {
                return <span className="text-white/35">No data yet</span>;
              }
              const arrow = _trendArrow(ctr, previous?.ctr ?? null);
              const pct = ctr != null ? `${Math.round(ctr * 100)}% CTR` : "—";
              return (
                <>
                  <span>{imp} shown</span>
                  <span className="text-white/25">·</span>
                  <span>{clk} clicked</span>
                  <span className="text-white/25">·</span>
                  <span>{pct}</span>
                  <span className={arrow.cls} data-testid={`sam-tip-trend-${row.module}`}>{arrow.glyph}</span>
                </>
              );
            })()}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onPreview}
            className="rounded-full border border-white/10 px-3 py-1 text-xs text-white/65 hover:bg-white/5"
            data-testid={`sam-tip-preview-${row.module}`}
          >
            Preview
          </button>
          <button
            type="button"
            onClick={reset}
            disabled={saving}
            className="rounded-full px-3 py-1 text-xs text-white/55 hover:text-white"
            data-testid={`sam-tip-reset-${row.module}`}
          >
            Reset to default
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving || msgInvalid || preInvalid}
            className="inline-flex items-center gap-1 rounded-full bg-white px-3 py-1 text-xs font-medium text-black hover:bg-zinc-200 disabled:opacity-60"
            data-testid={`sam-tip-save-${row.module}`}
          >
            {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

function SamTipPreviewDialog({ tip, onClose }) {
  const open = !!tip;
  const msg = (tip?.override_message || tip?.default_message) ?? "";
  const pre = (tip?.override_prefill || tip?.default_prefill) ?? "";
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl border-white/10 bg-[#0a0a0a] text-white" data-testid="sam-tip-preview-dialog">
        <DialogHeader>
          <DialogTitle>Preview — {tip?.label}</DialogTitle>
          <DialogDescription className="text-white/45">
            This is exactly how the card will render on {tip?.path}.
          </DialogDescription>
        </DialogHeader>
        <div className="mt-2 flex items-center gap-3 rounded-2xl border border-indigo-500/20 bg-gradient-to-r from-indigo-500/5 via-white/[0.02] to-teal-500/5 p-4">
          <div className="h-10 w-10 overflow-hidden rounded-full bg-zinc-800 ring-2 ring-indigo-500/30">
            <img src="/sam-avatar.png" alt="" className="h-full w-full object-cover" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-indigo-300">
              <Sparkles className="h-3 w-3" /> Suggested by Sam
            </div>
            <div className="mt-0.5 truncate text-sm text-zinc-200">{msg}</div>
          </div>
          <div className="flex-shrink-0 rounded-full bg-indigo-600 px-4 py-1.5 text-xs font-medium text-white">
            Ask Sam →
          </div>
        </div>
        <p className="mt-3 text-xs text-white/45">
          Clicking <span className="text-white/70">Ask Sam</span> would send:{" "}
          <span className="italic text-white/65">"{pre}"</span>
        </p>
      </DialogContent>
    </Dialog>
  );
}

function detectVideoLabel(url) {
  if (!url) return null;
  const u = url.trim();
  if (!u) return null;
  if (/(?:youtube\.com|youtu\.be)/i.test(u)) return "YouTube";
  if (/loom\.com\/(?:share|embed)/i.test(u)) return "Loom";
  if (/vimeo\.com/i.test(u)) return "Vimeo";
  if (/\.(mp4|webm|mov|m4v)(\?.*)?$/i.test(u)) return "MP4 / direct video";
  return null;
}

// ──────────────────────────────────────────────────────────────────────────
// Playbook editor — list, create, edit (with markdown preview), reorder, delete.
// ──────────────────────────────────────────────────────────────────────────

const PB_TRACKS = [
  { id: "sales",   label: "Sales"   },
  { id: "manager", label: "Manager" },
];

function PlaybookAdminTab() {
  const [track, setTrack] = useState("sales");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);          // current chapter being edited (or "new")
  const [reordering, setReordering] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/playbook/chapters?track=${track}`);
      setRows(r.data || []);
    } catch (e) { toast.error(errorMessage(e)); }
    finally { setLoading(false); }
  }, [track]);
  useEffect(() => { load(); }, [load]);

  const onCreate = () => {
    setEditing({
      id: null,
      track,
      chapter_number: (rows[rows.length - 1]?.chapter_number || 0) + 1,
      slug: "",
      title: "",
      summary: "",
      content_md: "",
    });
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this chapter? This also clears reader progress.")) return;
    try {
      await api.delete(`/admin/playbook/chapters/${id}`);
      toast.success("Chapter deleted");
      load();
    } catch (e) { toast.error(errorMessage(e)); }
  };

  const move = async (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= rows.length) return;
    const next = [...rows];
    [next[idx], next[j]] = [next[j], next[idx]];
    setRows(next);  // optimistic
    setReordering(true);
    try {
      await api.post("/admin/playbook/chapters/reorder", {
        track,
        chapter_ids: next.map((c) => c.id),
      });
    } catch (e) {
      toast.error(errorMessage(e));
      load();   // resync on failure
    } finally {
      setReordering(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="playbook-admin-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-eyebrow text-indigo-300">
            <BookOpen className="h-3.5 w-3.5" /> Playbook editor
          </div>
          <h2 className="font-display mt-2 text-2xl font-light text-white">Sales & Manager training chapters</h2>
          <p className="mt-1 max-w-2xl text-sm text-white/55">
            Edit the chapters every signed-in user sees at <code className="text-white/80">/app/playbook</code>.
            Changes are platform-wide and live immediately. Markdown supported.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div
            className="inline-flex shrink-0 items-center gap-0.5 rounded-full border border-white/10 bg-white/[0.03] p-0.5 text-xs"
            role="group"
            data-testid="admin-playbook-track-tabs"
          >
            {PB_TRACKS.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setTrack(t.id)}
                aria-pressed={track === t.id}
                className={`rounded-full px-3 py-1 transition ${
                  track === t.id ? "bg-white text-black" : "text-white/65 hover:bg-white/5 hover:text-white"
                }`}
                data-testid={`admin-playbook-track-${t.id}`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <Button
            onClick={onCreate}
            className="rounded-full bg-white text-black hover:bg-zinc-200"
            data-testid="admin-playbook-new"
          >
            <Plus className="mr-1 h-3.5 w-3.5" /> New chapter
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12 text-white/45"><Loader2 className="h-4 w-4 animate-spin" /></div>
      ) : rows.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-white/10 bg-white/[0.02] p-10 text-center text-white/55">
          No chapters yet for this track. Click <span className="text-white/80">New chapter</span> to start writing.
        </div>
      ) : (
        <div className="glass overflow-hidden rounded-2xl" data-testid="admin-playbook-table">
          <table className="w-full text-sm">
            <thead className="bg-white/5 text-white/55">
              <tr>
                <th className="w-16 px-4 py-2 text-left">#</th>
                <th className="px-4 py-2 text-left">Title</th>
                <th className="px-4 py-2 text-left">Slug</th>
                <th className="px-4 py-2 text-left">Updated</th>
                <th className="w-40 px-4 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c, i) => (
                <tr
                  key={c.id}
                  className="border-t border-white/5 hover:bg-white/[0.03]"
                  data-testid={`admin-playbook-row-${c.id}`}
                >
                  <td className="px-4 py-3 font-mono text-white/55">{c.chapter_number}</td>
                  <td className="px-4 py-3 text-white">
                    <button
                      onClick={() => setEditing(c)}
                      className="text-left hover:underline"
                      data-testid={`admin-playbook-edit-${c.id}`}
                    >
                      {c.title}
                    </button>
                    {c.summary && (
                      <div className="mt-0.5 line-clamp-1 text-xs text-white/45">{c.summary}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs font-mono text-white/55">{c.slug}</td>
                  <td className="px-4 py-3 text-xs text-white/45">
                    {c.updated_at ? new Date(c.updated_at).toLocaleString() : "—"}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => move(i, -1)}
                        disabled={i === 0 || reordering}
                        className="rounded p-1.5 text-white/55 hover:bg-white/10 hover:text-white disabled:opacity-25"
                        title="Move up"
                        data-testid={`admin-playbook-up-${c.id}`}
                      >
                        <ArrowUp className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={() => move(i, +1)}
                        disabled={i === rows.length - 1 || reordering}
                        className="rounded p-1.5 text-white/55 hover:bg-white/10 hover:text-white disabled:opacity-25"
                        title="Move down"
                        data-testid={`admin-playbook-down-${c.id}`}
                      >
                        <ArrowDown className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={() => onDelete(c.id)}
                        className="rounded p-1.5 text-red-300 hover:bg-red-500/10"
                        title="Delete"
                        data-testid={`admin-playbook-delete-${c.id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <PlaybookEditDrawer
        chapter={editing}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); load(); }}
      />
    </div>
  );
}

function PlaybookEditDrawer({ chapter, onClose, onSaved }) {
  const [form, setForm] = useState({ title: "", summary: "", content_md: "", track: "sales" });
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(true);

  useEffect(() => {
    if (chapter) {
      setForm({
        title: chapter.title || "",
        summary: chapter.summary || "",
        content_md: chapter.content_md || "",
        track: chapter.track || "sales",
        chapter_number: chapter.chapter_number,
      });
    }
  }, [chapter]);

  if (!chapter) return null;
  const isNew = chapter.id == null;

  const save = async () => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    setSaving(true);
    try {
      if (isNew) {
        await api.post("/admin/playbook/chapters", {
          title: form.title,
          summary: form.summary,
          content_md: form.content_md,
          track: form.track,
        });
        toast.success("Chapter created");
      } else {
        await api.patch(`/admin/playbook/chapters/${chapter.id}`, {
          title: form.title,
          summary: form.summary,
          content_md: form.content_md,
          track: form.track,
        });
        toast.success("Chapter saved");
      }
      onSaved?.();
    } catch (e) { toast.error(errorMessage(e)); }
    finally { setSaving(false); }
  };

  return (
    <Sheet open={!!chapter} onOpenChange={(o) => !o && onClose()}>
      <SheetContent
        side="right"
        className="w-full overflow-y-auto border-white/10 bg-[#0a0a0a] text-white sm:max-w-4xl"
        data-testid="admin-playbook-drawer"
      >
        <SheetHeader>
          <SheetTitle className="font-display text-2xl">
            {isNew ? "New chapter" : `Edit · Chapter ${chapter.chapter_number}`}
          </SheetTitle>
          <SheetDescription className="text-white/55">
            Track-scoped to <span className="text-white/85">{form.track === "sales" ? "Sales" : "Manager"}</span>.
            Markdown is rendered live in the preview panel.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-5 grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-white/50">Track</Label>
            <Select value={form.track} onValueChange={(v) => setForm({ ...form, track: v })}>
              <SelectTrigger className="border-white/10 bg-black/40 text-white" data-testid="admin-playbook-track-select"><SelectValue /></SelectTrigger>
              <SelectContent className="border-white/10 bg-[#0a0a0a] text-white">
                {PB_TRACKS.map((t) => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-white/50">Title *</Label>
            <Input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="border-white/10 bg-black/40 text-white"
              data-testid="admin-playbook-title"
            />
          </div>
        </div>

        <div className="mt-3 space-y-1.5">
          <Label className="text-xs uppercase tracking-wider text-white/50">Summary</Label>
          <Input
            value={form.summary}
            onChange={(e) => setForm({ ...form, summary: e.target.value })}
            placeholder="One-line description shown in the chapter rail."
            className="border-white/10 bg-black/40 text-white"
            data-testid="admin-playbook-summary"
          />
        </div>

        <div className="mt-4 flex items-center justify-between">
          <Label className="text-xs uppercase tracking-wider text-white/50">Markdown content</Label>
          <button
            type="button"
            onClick={() => setShowPreview((v) => !v)}
            className="text-xs text-white/55 hover:text-white"
            data-testid="admin-playbook-toggle-preview"
          >
            {showPreview ? "Hide preview" : "Show preview"}
          </button>
        </div>
        <div className={`mt-2 grid gap-3 ${showPreview ? "lg:grid-cols-2" : "grid-cols-1"}`}>
          <Textarea
            value={form.content_md}
            onChange={(e) => setForm({ ...form, content_md: e.target.value })}
            rows={24}
            className="border-white/10 bg-black/40 font-mono text-xs text-white/85"
            placeholder="# Chapter title&#10;&#10;Write the body in **markdown**. Lists, tables, links and code blocks all render."
            data-testid="admin-playbook-content"
          />
          {showPreview && (
            <div
              className="max-h-[600px] overflow-y-auto rounded-xl border border-white/8 bg-white/[0.02] p-5"
              data-testid="admin-playbook-preview"
            >
              {form.content_md.trim() ? (
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    h1: ({ children }) => <h1 className="font-display mt-1 mb-4 text-2xl font-light text-white">{children}</h1>,
                    h2: ({ children }) => <h2 className="font-display mt-6 mb-2 text-lg font-medium text-white">{children}</h2>,
                    h3: ({ children }) => <h3 className="mt-5 mb-2 text-base font-medium text-white">{children}</h3>,
                    p:  ({ children }) => <p className="my-2 text-sm leading-relaxed text-white/75">{children}</p>,
                    ul: ({ children }) => <ul className="my-2 list-disc space-y-1 pl-5 text-sm text-white/75 marker:text-white/30">{children}</ul>,
                    ol: ({ children }) => <ol className="my-2 list-decimal space-y-1 pl-5 text-sm text-white/75 marker:text-white/30">{children}</ol>,
                    strong: ({ children }) => <strong className="font-medium text-white">{children}</strong>,
                    a: ({ children, href }) => <a href={href} target="_blank" rel="noreferrer" className="text-indigo-300 underline-offset-4 hover:underline">{children}</a>,
                    code: ({ inline, children }) => inline
                      ? <code className="rounded bg-white/[0.06] px-1 py-0.5 text-[0.85em] text-indigo-200">{children}</code>
                      : <pre className="my-3 overflow-x-auto rounded border border-white/8 bg-black/40 p-3 text-[12px] text-white/85">{children}</pre>,
                    blockquote: ({ children }) => <blockquote className="my-3 border-l-2 border-indigo-500/40 bg-white/[0.02] py-1 pl-3 text-sm text-white/70">{children}</blockquote>,
                    hr: () => <hr className="my-5 border-white/8" />,
                  }}
                >
                  {form.content_md}
                </ReactMarkdown>
              ) : (
                <div className="py-12 text-center text-xs text-white/40">Preview will render here as you type.</div>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="mt-6 flex-row justify-end gap-2 sm:justify-end">
          <Button type="button" variant="ghost" onClick={onClose} className="text-white/65 hover:text-white">Cancel</Button>
          <Button
            type="button"
            onClick={save}
            disabled={saving}
            className="rounded-xl bg-white text-black hover:bg-zinc-200"
            data-testid="admin-playbook-save"
          >
            {saving ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Save className="mr-1.5 h-3.5 w-3.5" />}
            {isNew ? "Create chapter" : "Save changes"}
          </Button>
        </DialogFooter>
      </SheetContent>
    </Sheet>
  );
}




// ────────────────────── PUBLIC SAM — LEADS TAB ──────────────────────
function PublicSamLeadsTab() {
  const [rows, setRows] = useState(null);
  const [openSession, setOpenSession] = useState(null);
  const [detail, setDetail] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const r = await api.get("/admin/public-sam/conversations");
      setRows(r.data?.items || []);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally { setRefreshing(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!openSession) { setDetail(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/admin/public-sam/conversations/${openSession}`);
        if (!cancelled) setDetail(r.data);
      } catch (e) {
        if (!cancelled) toast.error(errorMessage(e));
      }
    })();
    return () => { cancelled = true; };
  }, [openSession]);

  if (rows === null) {
    return <div className="flex items-center justify-center py-16 text-white/45" data-testid="public-sam-leads-loading"><Loader2 className="h-5 w-5 animate-spin" /></div>;
  }
  return (
    <div className="space-y-4" data-testid="public-sam-leads-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-2xl font-light text-white">Public Sam · Visitor leads</h2>
          <p className="mt-1 text-sm text-white/55">Every fresh marketing-site chat captures the visitor as a lead in Acme&apos;s workspace.</p>
        </div>
        <Button variant="ghost" onClick={load} disabled={refreshing} className="text-white/65 hover:text-white" data-testid="public-sam-refresh">
          <RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>
      <RebrandBannerMetricsTile />
      {rows.length === 0 ? (
        <div className="rounded-2xl border border-white/8 bg-white/[0.02] p-10 text-center text-sm text-white/45" data-testid="public-sam-leads-empty">
          <MessageSquare className="mx-auto h-7 w-7 text-white/30" />
          <p className="mt-3">No public-Sam conversations yet. Visitor chats started on <span className="font-mono">/</span> and <span className="font-mono">/pricing</span> will appear here.</p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-white/8 bg-white/[0.02]" data-testid="public-sam-leads-table">
          <table className="w-full text-sm">
            <thead className="bg-white/[0.03] text-xs uppercase tracking-wider text-white/45">
              <tr>
                <th className="px-4 py-3 text-left">Visitor</th>
                <th className="px-4 py-3 text-left">Company</th>
                <th className="px-4 py-3 text-left">Last message</th>
                <th className="px-4 py-3 text-left"># msgs</th>
                <th className="px-4 py-3 text-left">Last seen</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.session_id} className="border-t border-white/5 text-white/85" data-testid={`public-sam-row-${r.session_id}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-white">{r.visitor_name || "—"}</div>
                    <div className="text-xs text-white/45">{r.visitor_email || "no email"}</div>
                  </td>
                  <td className="px-4 py-3 text-white/65">{r.visitor_company || "—"}</td>
                  <td className="px-4 py-3 text-white/70">
                    <div className="line-clamp-1 max-w-md">{r.last_message_preview || "—"}</div>
                  </td>
                  <td className="px-4 py-3 text-white/65">{r.message_count}</td>
                  <td className="px-4 py-3 text-xs text-white/45">{r.last_message_at ? new Date(r.last_message_at).toLocaleString() : "—"}</td>
                  <td className="px-4 py-3 text-right">
                    <Button variant="ghost" size="sm" onClick={() => setOpenSession(r.session_id)} className="text-white/65 hover:text-white" data-testid={`public-sam-view-${r.session_id}`}>
                      <Eye className="mr-1 h-3.5 w-3.5" /> View
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Sheet open={!!openSession} onOpenChange={(o) => { if (!o) setOpenSession(null); }}>
        <SheetContent className="w-full max-w-2xl overflow-y-auto border-l border-white/8 bg-[#0a0a0a] text-white sm:max-w-2xl" data-testid="public-sam-detail-sheet">
          <SheetHeader>
            <SheetTitle className="font-display text-2xl font-light text-white">Conversation transcript</SheetTitle>
            <SheetDescription className="text-white/55">
              {detail?.visitor_name || "—"} · {detail?.visitor_email || "no email"} {detail?.visitor_company ? `· ${detail.visitor_company}` : ""}
            </SheetDescription>
          </SheetHeader>
          {!detail ? (
            <div className="flex items-center justify-center py-16 text-white/45"><Loader2 className="h-5 w-5 animate-spin" /></div>
          ) : (
            <div className="mt-6 space-y-3" data-testid="public-sam-detail-messages">
              {(detail.messages || []).map((m, i) => (
                <div
                  key={i}
                  className={`rounded-2xl px-3 py-2 text-sm leading-relaxed ${m.role === "user" ? "bg-indigo-500/15 text-white" : "bg-white/[0.04] text-white/85"}`}
                  data-testid={`public-sam-detail-msg-${i}`}
                >
                  <div className="mb-1 text-[10px] uppercase tracking-wider text-white/40">{m.role}</div>
                  <div className="whitespace-pre-wrap">{m.content}</div>
                </div>
              ))}
              {(detail.messages || []).length === 0 && (
                <div className="text-sm text-white/50">No messages yet.</div>
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

// ────────────────────── PROMO CODES TAB ──────────────────────
function PromoCodesTab() {
  const [rows, setRows] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [toggling, setToggling] = useState({});

  const load = useCallback(async () => {
    try {
      const r = await api.get("/admin/promo-codes");
      setRows(r.data?.items || []);
    } catch (e) { toast.error(errorMessage(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const sync = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/admin/promo-codes/sync");
      toast.success(`Synced ${r.data?.synced ?? 0} code(s) · ${r.data?.deactivated ?? 0} deactivated`);
      await load();
    } catch (e) { toast.error(errorMessage(e)); }
    finally { setSyncing(false); }
  };

  const toggleSurface = async (code, next) => {
    setToggling((t) => ({ ...t, [code]: true }));
    try {
      await api.patch(`/admin/promo-codes/${code}`, { surface_on_pricing_page: next });
      setRows((rs) => (rs || []).map((r) => r.code === code ? { ...r, surface_on_pricing_page: next } : r));
    } catch (e) { toast.error(errorMessage(e)); }
    finally { setToggling((t) => ({ ...t, [code]: false })); }
  };

  const formatSavings = (r) => {
    if (r.amount_off_cents) return `$${Math.round(r.amount_off_cents / 100)} off`;
    if (r.percent_off) return `${Math.round(r.percent_off)}% off`;
    return "—";
  };
  const formatDuration = (r) => {
    if (r.duration === "forever") return "for life";
    if (r.duration === "once") return "first invoice";
    if (r.duration === "repeating") return `${r.duration_in_months || "?"} months`;
    return r.duration || "—";
  };
  const formatExpires = (r) => {
    if (!r.expires_at) return "—";
    try { return new Date(r.expires_at * 1000).toLocaleDateString(); }
    catch { return "—"; }
  };

  if (rows === null) {
    return <div className="flex items-center justify-center py-16 text-white/45" data-testid="promo-codes-loading"><Loader2 className="h-5 w-5 animate-spin" /></div>;
  }
  return (
    <div className="space-y-4" data-testid="promo-codes-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-2xl font-light text-white">Promo codes · Stripe sync</h2>
          <p className="mt-1 text-sm text-white/55">Stripe is the source of truth. Sync pulls every active code, then toggle which ones surface on the public pricing page.</p>
        </div>
        <Button onClick={sync} disabled={syncing} className="rounded-xl bg-white text-black hover:bg-zinc-200" data-testid="promo-sync-btn">
          {syncing ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="mr-1.5 h-3.5 w-3.5" />} Sync from Stripe
        </Button>
      </div>

      {rows.length === 0 ? (
        <div className="rounded-2xl border border-white/8 bg-white/[0.02] p-10 text-center text-sm text-white/45" data-testid="promo-codes-empty">
          <Tag className="mx-auto h-7 w-7 text-white/30" />
          <p className="mt-3">No promo codes synced yet. Click <span className="font-medium text-white">Sync from Stripe</span> to import every active promotion code.</p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-white/8 bg-white/[0.02]" data-testid="promo-codes-table">
          <table className="w-full text-sm">
            <thead className="bg-white/[0.03] text-xs uppercase tracking-wider text-white/45">
              <tr>
                <th className="px-4 py-3 text-left">Code</th>
                <th className="px-4 py-3 text-left">Savings</th>
                <th className="px-4 py-3 text-left">Duration</th>
                <th className="px-4 py-3 text-left">Active</th>
                <th className="px-4 py-3 text-left">Redemptions</th>
                <th className="px-4 py-3 text-left">Expires</th>
                <th className="px-4 py-3 text-left">Show on pricing page</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.code} className="border-t border-white/5 text-white/85" data-testid={`promo-row-${r.code}`}>
                  <td className="px-4 py-3">
                    <div className="font-mono text-sm font-medium text-white">{r.code}</div>
                    {r.coupon_id && <div className="text-[10px] text-white/35">{r.coupon_id}</div>}
                  </td>
                  <td className="px-4 py-3 text-white/75">{formatSavings(r)}</td>
                  <td className="px-4 py-3 text-white/65">{formatDuration(r)}</td>
                  <td className="px-4 py-3">
                    {r.is_active ? (
                      <span className="inline-flex items-center gap-1 rounded-full border border-emerald-500/25 bg-emerald-500/10 px-2 py-0.5 text-[11px] text-emerald-200" data-testid={`promo-active-${r.code}`}><CheckCircle2 className="h-3 w-3" /> active</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-full border border-white/10 bg-white/[0.03] px-2 py-0.5 text-[11px] text-white/45" data-testid={`promo-active-${r.code}`}><XCircle className="h-3 w-3" /> inactive</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-white/65">{r.times_redeemed ?? 0}{r.max_redemptions ? ` / ${r.max_redemptions}` : ""}</td>
                  <td className="px-4 py-3 text-xs text-white/55">{formatExpires(r)}</td>
                  <td className="px-4 py-3">
                    <Switch
                      checked={!!r.surface_on_pricing_page}
                      onCheckedChange={(v) => toggleSurface(r.code, v)}
                      disabled={!!toggling[r.code] || !r.is_active}
                      data-testid={`promo-surface-${r.code}`}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="text-xs text-white/35">Inactive codes from Stripe are auto-deactivated here. Toggle <span className="text-white/65">Show on pricing page</span> to drive the badge on the public pricing page.</p>
    </div>
  );
}


// ────────────────────── REBRAND BANNER · METRICS TILE (Iter 18) ──────────────────────
function RebrandBannerMetricsTile() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/admin/rebrand-banner/metrics");
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) setErr(errorMessage(e));
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const fmtSecs = (s) => {
    if (!s || s < 60) return `${s ?? 0}s`;
    if (s < 3600) return `${Math.round(s / 60)}m`;
    if (s < 86400) return `${Math.round(s / 3600)}h`;
    return `${Math.round(s / 86400)}d`;
  };

  if (err) return null;
  if (!data) {
    return (
      <div className="rounded-2xl border border-white/8 bg-white/[0.02] p-5 text-xs text-white/45" data-testid="rebrand-metrics-loading">
        <Loader2 className="h-4 w-4 animate-spin" />
      </div>
    );
  }
  // If nobody is eligible (post-launch deploys), the tile is hidden — keeps
  // admin focused on what matters today.
  if (!data.eligible_users_total) return null;

  const cell = (label, value, testid) => (
    <div className="flex flex-col" data-testid={testid}>
      <span className="font-display text-2xl font-light text-white tabular-nums">{value}</span>
      <span className="mt-0.5 text-[11px] uppercase tracking-wider text-white/45">{label}</span>
    </div>
  );

  return (
    <div className="glass-heavy relative overflow-hidden rounded-2xl p-5 sm:p-6" data-testid="rebrand-metrics-tile">
      <div className="absolute inset-x-0 top-0 h-0.5 brand-bg" />
      <div className="mb-4">
        <h3 className="text-sm font-medium text-white">Rebrand banner · response</h3>
        <p className="mt-0.5 text-xs text-white/45">How pre-rebrand customers responded to the rename.</p>
      </div>
      <div className="grid grid-cols-2 gap-5 sm:grid-cols-4">
        {cell("Eligible users", data.eligible_users_total, "rebrand-metric-eligible")}
        {cell("Saw banner", data.seen_count, "rebrand-metric-seen")}
        {cell("Dismissed", data.dismissed_count, "rebrand-metric-dismissed")}
        {cell("Dismissed ≤ 7d", data.dismissed_within_7d_count, "rebrand-metric-7d")}
      </div>
      <div className="mt-4 text-[11px] text-white/35">
        Avg time-to-dismiss: <span className="text-white/55 tabular-nums">{fmtSecs(data.avg_time_to_dismiss_seconds)}</span>
        <span className="mx-2">·</span>
        Announce date: <span className="text-white/55">{new Date(data.announce_date).toLocaleDateString()}</span>
        <span className="mx-2">·</span>
        Window: <span className="text-white/55">{data.ttl_days}d</span>
      </div>
    </div>
  );
}

